import { useEffect, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/ui/Button";
import { TextField } from "~/components/ui/TextField";
import { BodyText, CaptionText, Heading3 } from "~/components/ui/Text";
import { useAuth } from "~/hooks/useAuth";
import { usePurchases } from "~/hooks/usePurchases";
import {
  cancelDeletion,
  getDeletionRequest,
  requestDeletion,
  type DeletionRequest,
} from "~/services/account-data";
import {
  accountDeletionBlocker,
  isDeletionConfirmed,
} from "~/services/account-deletion";
import { spacing, useTheme } from "~/styles/theme";

/**
 * In-app account deletion (an App Store / Google Play requirement for apps
 * that let people create accounts). Three steps: a "Delete account" button, an
 * explicit confirmation where the person types DELETE and their email address,
 * and a 30-day grace period during which they stay signed in and can cancel.
 * After that a daily job deletes the account and everything stored for it.
 *
 * It refuses while the subscription is still set to renew, because deleting the
 * account would not stop the billing. If the subscription status can't be
 * checked, it refuses too rather than risk orphaning a paying customer.
 */
export function DeleteAccount() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const purchases = usePurchases(user?.id ?? null);
  const [request, setRequest] = useState<DeletionRequest | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [typedEmail, setTypedEmail] = useState("");
  const [pending, setPending] = useState<"" | "delete" | "cancel">("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getDeletionRequest()
      .then((pendingRequest) => {
        if (active) setRequest(pendingRequest);
      })
      .catch(() => {
        // Still usable without it; the request path re-checks.
      });
    return () => {
      active = false;
    };
  }, []);

  const blocker = accountDeletionBlocker(purchases.customerInfo);
  const statusUnknown = purchases.loading || Boolean(purchases.error);
  const confirmed = isDeletionConfirmed({
    phrase,
    email: typedEmail,
    accountEmail: user?.email ?? "",
  });

  const reset = () => {
    setConfirming(false);
    setPhrase("");
    setTypedEmail("");
    setError("");
  };

  const confirmDelete = async () => {
    setPending("delete");
    setError("");
    try {
      setRequest(await requestDeletion(typedEmail));
      reset();
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : "Could not schedule the deletion.",
      );
    } finally {
      setPending("");
    }
  };

  const cancel = async () => {
    setPending("cancel");
    setError("");
    try {
      await cancelDeletion();
      setRequest(null);
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : "Could not cancel the deletion.",
      );
    } finally {
      setPending("");
    }
  };

  if (request) {
    return (
      <View style={{ gap: spacing.md }}>
        <Heading3>Account deletion scheduled</Heading3>
        <BodyText color={colors.muted}>
          Your account and every counter saved to it will be deleted on{" "}
          {new Date(request.scheduledFor).toLocaleDateString()}. Until then you
          can cancel and keep everything.
        </BodyText>
        {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}
        <Button
          label={pending === "cancel" ? "Cancelling…" : "Cancel deletion"}
          variant="secondary"
          fullWidth
          disabled={pending !== ""}
          onPress={() => void cancel()}
        />
      </View>
    );
  }

  if (!confirming) {
    return (
      <Button
        label="Delete account"
        variant="danger"
        fullWidth
        onPress={() => {
          setError("");
          setConfirming(true);
        }}
      />
    );
  }

  return (
    <View style={{ gap: spacing.md }}>
      <Heading3>Delete your account?</Heading3>

      {blocker === "renewing-subscription" ? (
        <BodyText color={colors.muted}>
          Your subscription is still set to renew, and deleting your account
          doesn’t cancel it — you would keep being billed. Cancel it first with
          “Manage subscription” above, then come back to delete your account.
        </BodyText>
      ) : (
        <View style={{ gap: spacing.xs }}>
          <BodyText color={colors.muted}>
            This permanently deletes your account and every counter saved to it,
            30 days from now. You can cancel any time before then. Counters that
            only exist on this device are not affected.
          </BodyText>
          {user?.hasSyncAccess ? (
            <CaptionText color={colors.muted}>
              Any time left on your subscription or free trial is forfeited.
            </CaptionText>
          ) : null}
        </View>
      )}

      {purchases.error && !blocker ? (
        <BodyText color={colors.danger}>
          We couldn’t check your subscription status, so we can’t delete your
          account safely right now. Try again shortly.
        </BodyText>
      ) : null}

      {blocker ? null : (
        <View style={{ gap: spacing.sm }}>
          <TextField
            label="Type DELETE to confirm"
            value={phrase}
            onChangeText={setPhrase}
            autoCapitalize="characters"
            autoCorrect={false}
          />
          <TextField
            label="Type your email address"
            value={typedEmail}
            onChangeText={setTypedEmail}
            placeholder={user?.email}
            autoCapitalize="none"
            autoComplete="email"
            autoCorrect={false}
            keyboardType="email-address"
          />
        </View>
      )}

      {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}

      <View style={{ gap: spacing.sm }}>
        {blocker ? null : (
          <Button
            label={
              pending === "delete"
                ? "Scheduling…"
                : purchases.loading
                  ? "Checking your subscription…"
                  : "Delete my account"
            }
            variant="danger"
            fullWidth
            disabled={!confirmed || pending !== "" || statusUnknown}
            onPress={() => void confirmDelete()}
          />
        )}
        <Button
          label={blocker ? "Back" : "Keep my account"}
          variant="secondary"
          fullWidth
          disabled={pending !== ""}
          onPress={reset}
        />
      </View>
    </View>
  );
}
